import React from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_LIFTPLAN,
  GET_ALL_LIFTPLANS,
  LINK_LIFTPLAN_JOBS,
  REMOVE_LIFTPLAN,
  UPDATE_LIFTPLAN,
} from '../../../graph-queries/tar/liftplan';
import { TLiftPlan } from '../../../lib/types';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import LiftplanForm from '../../organisms/LiftplanForm/LiftplanForm2';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import { useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import {
  LIFTPLAN_EQUIPMENT_STATUS_ENUM,
  LIFTPLAN_STATUS_ENUM,
  LIFTPLAN_STRUCTURAL_ASSESSMENT_ENUM,
  LIFTPLAN_TYPE_ENUM,
} from '../../../lib/constants/dbConstants';

const renderFromBoolean = <T, V>(props: CellContext<T, V>) => {
  return props?.getValue() ? 'Yes' : 'No';
};

export const columns: ColumnDef<TLiftPlan>[] = [
  {
    header: 'Plan Number',
    accessorKey: 'planNumber',
    enableSorting: false,
  },
  {
    header: 'Description',
    accessorKey: 'description',
    enableSorting: false,
  },
  {
    header: 'Type',
    accessorKey: 'type',
    enableSorting: false,
    accessorFn: (row) => LIFTPLAN_TYPE_ENUM[row.type],
  },
  {
    header: 'Lifting Equipment',
    accessorKey: 'equipmentStatus',
    enableSorting: false,
    accessorFn: (row) => LIFTPLAN_EQUIPMENT_STATUS_ENUM[row.equipmentStatus],
  },
  {
    header: 'Structural Assessment',
    accessorKey: 'structuralAssessment',
    enableSorting: false,
    accessorFn: (row) => LIFTPLAN_STRUCTURAL_ASSESSMENT_ENUM[row.structuralAssessment],
  },
  {
    header: 'Safe Working Limit (kg)',
    accessorKey: 'safeWorking',
    enableSorting: false,
  },
  {
    header: 'Erect Hours',
    accessorKey: 'erectHours',
    enableSorting: false,
  },
  {
    header: 'Deconstruct Hours',
    accessorKey: 'deconstructHours',
    enableSorting: false,
  },
  {
    header: 'Overside Cover',
    accessorKey: 'outsideCover',
    enableSorting: false,
    cell: renderFromBoolean,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    enableSorting: false,
    accessorFn: (row) => LIFTPLAN_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const LiftplansPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const { data, loading } = useQuery(GET_ALL_LIFTPLANS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const [createLiftPlan, { loading: isCreating }] = useMutation(CREATE_LIFTPLAN, {
    refetchQueries: [GET_ALL_LIFTPLANS],
    awaitRefetchQueries: true,
  });
  const [updateLiftPlan, { loading: isUpdating }] = useMutation(UPDATE_LIFTPLAN, {
    refetchQueries: [GET_ALL_LIFTPLANS],
    awaitRefetchQueries: true,
  });
  const [linkLiftPlanJobs, { loading: isLinking }] = useMutation(LINK_LIFTPLAN_JOBS, {
    refetchQueries: [GET_ALL_LIFTPLANS],
    awaitRefetchQueries: true,
  });
  const [removeLiftPlan, { loading: isRemoving }] = useMutation(REMOVE_LIFTPLAN, {
    refetchQueries: [GET_ALL_LIFTPLANS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (liftPlan: TLiftPlan) => {
    const { liftPlanId, updatedBy, updatedAt, tarJobs, ...input } = liftPlan;
    return createLiftPlan({ variables: { input } }).then(() =>
      setAlertText('Liftplan successfully added')
    );
  };
  const onEditSubmit = (liftPlan: TLiftPlan) => {
    const { updatedBy, updatedAt, tarJobs, ...input } = liftPlan;
    return updateLiftPlan({ variables: { input } }).then(() =>
      setAlertText('Liftplan successfully updated')
    );
  };

  const onLinkSubmit = (selection: TLiftPlan[], jobIds: number[]) => {
    return Promise.all(
      selection.map((liftPlan) =>
        linkLiftPlanJobs({
          variables: {
            liftPlanId: liftPlan.liftPlanId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Liftplan successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TLiftPlan[]) => {
    return Promise.all(
      selection.map((liftPlan) =>
        removeLiftPlan({
          variables: { liftPlanId: liftPlan.liftPlanId },
        })
      )
    ).then(() => setAlertText(`Liftplan successfully deleted`));
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TLiftPlan>
            hideSelectionColumn
            aspectName="Lift Plan"
            data={(data?.allLiftPlans as unknown as TLiftPlan[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <LiftplanForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <LiftplanForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the lift plan tracker..."
            isLoading={isLoading}
          />
        )}
      </Container>
    </Layout>
  );
};

export default LiftplansPage;
